import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollOptions, ScrollState } from '../types/infiniteScroll';

interface UseScrollDetectionProps extends ScrollOptions {
  onReachThreshold: () => void;
  enabled?: boolean;
}

export const useScrollDetection = ({
  threshold = 200,
  scrollContainer = null,
  debounceMs = 150,
  onReachThreshold,
  enabled = true,
}: UseScrollDetectionProps) => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    isNearBottom: false,
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    container: null,
  });

  // Ref for the sentinel element (last item indicator)
  const sentinelRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLElement | Window | null>(null);
  const lastTriggeredRef = useRef<number>(0);

  const getScrollContainer = useCallback((): HTMLElement | Window => {
    if (typeof scrollContainer === 'string') {
      const element = document.querySelector(scrollContainer) as HTMLElement;
      return element || window;
    }

    if (scrollContainer instanceof HTMLElement) {
      return scrollContainer;
    }

    return window;
  }, [scrollContainer]);

  const getScrollMeasurements = useCallback((container: HTMLElement | Window) => {
    if (container === window) {
      return {
        scrollTop: window.pageYOffset || document.documentElement.scrollTop,
        scrollHeight: Math.max(document.body.scrollHeight, document.documentElement.scrollHeight),
        clientHeight: window.innerHeight,
      };
    } else {
      const element = container as HTMLElement;
      return {
        scrollTop: element.scrollTop,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight,
      };
    }
  }, []);

  // Debounced threshold trigger
  const triggerThreshold = useCallback(() => {
    if (!enabled) return;

    const now = Date.now();
    if (now - lastTriggeredRef.current < debounceMs) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      lastTriggeredRef.current = Date.now();
      onReachThreshold();
    }, debounceMs);
  }, [enabled, debounceMs, onReachThreshold]);

  // Update scroll state
  const updateScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    try {
      const measurements = getScrollMeasurements(container);

      setScrollState({
        isNearBottom: true, // Always true when sentinel is visible
        ...measurements,
        container,
      });
    } catch (error) {
      console.error('Error updating scroll state:', error);
    }
  }, [getScrollMeasurements]);

  // Setup Intersection Observer
  useEffect(() => {
    if (!enabled) {
      // Clean up observer when disabled
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const container = getScrollContainer();
    containerRef.current = container;

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    const observerOptions: IntersectionObserverInit = {
      root: container === window ? null : (container as HTMLElement),
      rootMargin: `${threshold}px`,
      threshold: [0, 0.1, 0.5, 1.0], // Multiple thresholds for better detection
    };

    console.log('[ScrollDetection] Creating Intersection Observer with options:', observerOptions);

    observerRef.current = new IntersectionObserver((entries) => {
      console.log(
        '[ScrollDetection] Intersection entries:',
        entries.map((e) => ({
          isIntersecting: e.isIntersecting,
          intersectionRatio: e.intersectionRatio,
          target: e.target,
          isSentinel: e.target === sentinelRef.current,
        })),
      );

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target === sentinelRef.current) {
          console.log('[ScrollDetection] Sentinel is intersecting! Triggering threshold...');
          updateScrollState();
          triggerThreshold();
        }
      });
    }, observerOptions);

    // Start observing the sentinel if it exists
    if (sentinelRef.current) {
      console.log('[ScrollDetection] Starting to observe sentinel element:', sentinelRef.current);
      observerRef.current.observe(sentinelRef.current);
    } else {
      console.log('[ScrollDetection] No sentinel element to observe yet');
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, threshold]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Method to attach sentinel to an element
  const attachSentinel = useCallback(
    (element: HTMLElement | null) => {
      console.log('[ScrollDetection] attachSentinel called with element:', element);

      if (sentinelRef.current === element) {
        console.log('[ScrollDetection] Same sentinel element, skipping');
        return;
      }

      // Stop observing old sentinel
      if (observerRef.current && sentinelRef.current) {
        console.log('[ScrollDetection] Stopping observation of old sentinel:', sentinelRef.current);
        observerRef.current.unobserve(sentinelRef.current);
      }

      sentinelRef.current = element;

      // Start observing new sentinel
      if (observerRef.current && element && enabled) {
        console.log('[ScrollDetection] Starting observation of new sentinel:', element);
        observerRef.current.observe(element);

        // Force a check immediately to see if it's already visible
        setTimeout(() => {
          if (element && observerRef.current) {
            const entries = observerRef.current.takeRecords();
            console.log('[ScrollDetection] Immediate check entries:', entries);
          }
        }, 100);
      } else {
        console.log(
          '[ScrollDetection] Cannot observe sentinel - observer:',
          !!observerRef.current,
          'element:',
          !!element,
          'enabled:',
          enabled,
        );
      }
    },
    [enabled],
  );

  const forceCheck = useCallback(() => {
    if (enabled && sentinelRef.current) {
      // Force a check by creating a temporary observer
      const container = containerRef.current;
      const tempObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              updateScrollState();
              triggerThreshold();
            }
          });
          tempObserver.disconnect();
        },
        {
          root: container === window ? null : (container as HTMLElement),
          rootMargin: `${threshold}px`,
          threshold: 0.1,
        },
      );

      tempObserver.observe(sentinelRef.current);
    }
  }, [enabled, threshold, triggerThreshold, updateScrollState]);

  const scrollToTop = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (container === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      (container as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (container === window) {
      const scrollHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      window.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    } else {
      const element = container as HTMLElement;
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  return {
    scrollState,
    forceCheck,
    scrollToTop,
    scrollToBottom,
    attachSentinel,
    isNearBottom: scrollState.isNearBottom,
    container: containerRef.current,
  };
};

export const useScrollContainerRef = () => {
  const containerRef = useRef<HTMLElement>(null);

  return {
    containerRef,
    scrollContainer: containerRef.current,
  };
};
